import React, { ChangeEvent, useCallback, useEffect } from 'react';
import { availabilityOptions } from './AvailabilityFilterInput';
import { locationPreferenceOptionsData } from './LocationFilterInput';
import { roleOptions } from './RoleFilterInput';
import { CandidatesTabsEnum } from 'src/components/recruiterPositions/RecruiterCandidatesContainer2';
import { Spinner, TextInput } from 'flowbite-react';
import { TbSearch } from 'react-icons/tb';
import { CampusUserWithApplication, SkillType, TypeOfUsers } from 'types';
import { debounce } from 'lodash';
import { useSearchParams } from 'react-router-dom';
import { IoClose } from 'react-icons/io5';
import { useGetAllSkills } from 'src/hooks/skill.hooks';
import { educationOptions } from './EducationFilterInput';
import { PositionCandidatesTabsEnum } from '../../freshersPosition/PositionCandidateListingTabs';
import { useGetCollegeName } from 'src/hooks/collegeName.hooks';
import classNames from 'classnames';
import BulkSelectAction from './BulkSelectAction';
import RenderFilters from './RenderFilters';
import { statusOptionsData } from './StatusFilterInput';

export type FilterType = {
	filterKey: string;
	data: { label: string; value: string }[];
}[];

const CandidateFilters = ({
	resetPage,
	candidates,
	selectedTab,
	typeOfUsers,
	showCampusFilter,
	showEducationFilter,
	bulkSelectedCandidates,
	isApplicationApiLoading,
	setBulkSelectedCandidates,
}: {
	resetPage?: () => void;
	typeOfUsers?: TypeOfUsers;
	showCampusFilter?: boolean;
	showEducationFilter?: boolean;
	isApplicationApiLoading: boolean;
	candidates: CampusUserWithApplication[];
	bulkSelectedCandidates: string[];
	selectedTab: CandidatesTabsEnum | PositionCandidatesTabsEnum;
	setBulkSelectedCandidates: React.Dispatch<React.SetStateAction<string[]>>;
}) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const bulkSelect = searchParams.get('bulkSelect') === 'true';
	const [filterApplied, setFilterApplied] = React.useState(false);
	const [showFilterDrawer, setShowFilterDrawer] = React.useState(false);
	const [search, setSearch] = React.useState('');
	const [appliedFilters, setAppliedFilters] = React.useState<FilterType>([]);
	const { data: skillsData } = useGetAllSkills({ type: SkillType.SKILL });
	const [collegeOptions, setCollegeOptions] = React.useState<{ label: string; value: string }[]>([]);

	const { isLoading } = useGetCollegeName({
		onComplete: data => {
			const newColleges = data.collegeNames;
			const uniqueColleges = Array.from(new Set(newColleges.map(c => c.name)))
				.map(name => newColleges.find(c => c.name === name)!)
				.map(c => ({
					label: c.name,
					value: c._id,
				}));
			setCollegeOptions(uniqueColleges);
		},
	});

	const skillNameMap = new Map(skillsData?.skills?.map(skill => [skill._id, skill.name.replaceAll('_', ' ')]));

	const searchChangeHandler = (event: ChangeEvent<HTMLInputElement | HTMLInputElement>) => {
		searchParams.set('search', event.target.value);
		setSearchParams(searchParams);
	};

	const debouncedSearchChangeHandler = useCallback(debounce(searchChangeHandler, 1000), [search]);

	const removeFilter = (filterKey: string, value?: string) => {
		if (value) {
			const currentValues = JSON.parse(searchParams.get(filterKey) || '[]');
			const updatedValues = currentValues.filter((v: string) => v !== value);
			if (updatedValues.length > 0) {
				searchParams.set(filterKey, JSON.stringify(updatedValues));
			} else {
				searchParams.delete(filterKey);
			}
			setSearchParams(searchParams);
		}
	};

	useEffect(() => {
		const filters: FilterType = [];

		// Get availability filter
		const availability = JSON.parse(searchParams.get('availability') || '[]');
		if (availability.length) {
			filters.push({
				filterKey: 'availability',
				data: availabilityOptions
					?.filter(option => availability?.includes(option?.value))
					?.map(option => ({ label: option?.label, value: option?.value })),
			});
		}

		// Get location filter
		const location = JSON.parse(searchParams.get('location') || '[]');

		if (location.length) {
			filters.push({
				filterKey: 'location',
				data: locationPreferenceOptionsData
					?.filter(option => location?.includes(option?.value))
					?.map(option => ({
						label: option?.label,
						value: option?.value,
					})),
			});
		}

		// Get skill filter
		const skill = JSON.parse(searchParams.get('skills') || '[]');
		if (skill.length) {
			filters.push({
				filterKey: 'skills',
				data: skill.map((id: string) => ({
					label: skillNameMap.get(id) || id, // Use skill name or ID if not found
					value: id,
				})),
			});
		}

		// Get matched percentile filter
		const matchedPercentile = JSON.parse(searchParams.get('matchedpercentilefilter') || '[]');
		if (matchedPercentile.length) {
			filters.push({
				filterKey: 'matchedpercentilefilter',
				data: matchedPercentile.map((id: string) => ({
					label: id,
					value: id,
				})),
			});
		}

		// Get salary filter
		const salary = JSON.parse(searchParams.get('salary') || '[]');
		// if (salary) {
		// 	filters.push({
		// 		filterKey: 'salary',
		// 		data: [{ label: `${salary?.split('_')[0]}-${salary?.split('_')[1]} LPA`, value: salary }],
		// 	});
		// }
		if (salary?.length) {
			filters.push({
				filterKey: 'salary',
				data: salary.map((el: string) => ({ label: `${el?.split('_')[0]} to ${el?.split('_')[1]} LPA`, value: el })),
			});
		}

		// Get role filter
		const role = JSON.parse(searchParams.get('role') || '[]');
		if (role.length) {
			filters.push({
				filterKey: 'role',
				data: roleOptions
					?.filter(option => role?.includes(option?.value))
					?.map(option => ({
						label: option?.label,
						value: option?.value,
					})),
			});
		}

		// Get education filter
		const education = JSON.parse(searchParams.get('education') || '[]');
		if (education.length) {
			filters.push({
				filterKey: 'education',
				data: educationOptions
					?.filter(option => education?.includes(option))
					?.map(option => ({
						label: option,
						value: option,
					})),
			});
		}

		// Get status filter
		const status = JSON.parse(searchParams.get('status') || '[]');
		if (status.length) {
			filters.push({
				filterKey: 'status',
				data: statusOptionsData?.filter(option => status?.includes(option?.value)),
			});
		}

		const campus = JSON.parse(searchParams.get('campus') || '[]');
		if (campus.length) {
			filters.push({
				filterKey: 'campus',
				data: campus.map((id: string) => ({
					label: collegeOptions?.find(c => c.value === id)?.label || id, // Use campus name or ID if not found
					value: id,
				})),
			});
		}
		setAppliedFilters(filters);

		const bulkSelect = searchParams.get('bulkSelect') === 'true';

		// Check if any other search parameters have changed
		const otherParamsChanged = [
			'availability',
			'location',
			'skills',
			'salary',
			'role',
			'matchedpercentilefilter',
			'education',
			'campus',
			'search',
		].some(param => searchParams.has(param));

		if (!bulkSelect && otherParamsChanged) {
			resetPage && resetPage();
		}
	}, [searchParams, skillsData, collegeOptions]);

	return (
		<>
			{selectedTab === CandidatesTabsEnum.RECOMMENDATIONS ? (
				<>
					<RenderFilters
						showCampusFilter={!!showCampusFilter}
						showEducationFilter={!!showEducationFilter}
						showLocationFilter={true}
						showAvailabilityFilter={true}
						showRoleFilter={true}
						showSkillFilter={true}
						showMatchFilter={true}
						showMatchedPercentileFilter={true}
						showStatusFilter={typeOfUsers === TypeOfUsers.ASSESSMENT}
						showSalaryFilter={true}
						appliedFilters={appliedFilters}
						showFilterDrawer={showFilterDrawer}
						setAppliedFilters={setAppliedFilters}
						setShowFilterDrawer={setShowFilterDrawer}
						filterOrder={['availability', 'location', 'skill', 'role', 'campus', 'salary', 'education', 'status']}
					/>

					{appliedFilters.length > 0 && (
						<div className="mt-[12px] flex-wrap gap-4 hidden xl:flex">
							{appliedFilters
								?.filter(el => el?.data?.length)
								.map(filter => (
									<div key={filter.filterKey} className="flex flex-wrap items-center gap-4">
										{filter.data.map((value, index) => (
											<div
												key={index}
												className="text-sm font-[500] flex gap-1 bg-[#E4E4EB] text-[#555770] pl-[6px] rounded-[4px]"
											>
												<span className="py-[2px] max-w-[30ch] truncate">{value?.label}</span>
												<button
													onClick={() => removeFilter(filter.filterKey, value?.value)}
													className="hover:bg-gray-300 active:bg-gray-400 px-[6px] rounded-r-[4px]"
												>
													<IoClose className="w-[12px] h-[12px]" />
												</button>
											</div>
										))}
									</div>
								))}
						</div>
					)}
				</>
			) : null}

			{selectedTab !== CandidatesTabsEnum.RECOMMENDATIONS ? (
				<>
					<div
						className={classNames(
							'w-full md:mt-0 flex items-center gap-[12px] justify-stretch',
							typeOfUsers === TypeOfUsers.ASSESSMENT ? 'max-w-full' : 'md:max-w-[522px] ',
						)}
					>
						{/* Search Input */}
						<search className="w-full max-w-[400px]">
							<TextInput
								name="search"
								defaultValue={search}
								className="w-full [&_input]:bg-white"
								icon={() => <TbSearch color="#C7C9D9" />}
								onChange={e => debouncedSearchChangeHandler(e)}
								placeholder="Search candidate (ex - Vibhav, Aman)"
								rightIcon={() => (isApplicationApiLoading ? <Spinner /> : null)}
							/>
						</search>

						{typeOfUsers === TypeOfUsers.ASSESSMENT ? (
							<RenderFilters
								filterName=""
								showCampusFilter={false}
								showEducationFilter={false}
								showLocationFilter={false}
								showAvailabilityFilter={false}
								showRoleFilter={false}
								showMatchedPercentileFilter={true}
								showMatchFilter={false}
								showSkillFilter={typeOfUsers === TypeOfUsers.ASSESSMENT}
								showStatusFilter={typeOfUsers === TypeOfUsers.ASSESSMENT}
								showSalaryFilter={true}
								appliedFilters={appliedFilters}
								showFilterDrawer={showFilterDrawer}
								setAppliedFilters={setAppliedFilters}
								setShowFilterDrawer={setShowFilterDrawer}
							/>
						) : null}

						<BulkSelectAction
							bulkSelectedIds={bulkSelectedCandidates}
							setBulkSelectedIds={setBulkSelectedCandidates}
							typeOfUsers={typeOfUsers || TypeOfUsers.CAMPUS}
						/>
					</div>

					{appliedFilters.length > 0 && (
						<div className="mt-[12px] flex-wrap gap-4 hidden xl:flex">
							{appliedFilters
								?.filter(el => el?.data?.length)
								.map(filter => (
									<div key={filter.filterKey} className="flex flex-wrap items-center gap-4">
										{filter.data.map((value, index) => (
											<div
												key={index}
												className="text-sm font-[500] flex gap-1 bg-[#E4E4EB] text-[#555770] pl-[6px] rounded-[4px]"
											>
												<span className="py-[2px] max-w-[30ch] truncate">{value?.label}</span>
												<button
													onClick={() => removeFilter(filter.filterKey, value?.value)}
													className="hover:bg-gray-300 active:bg-gray-400 px-[6px] rounded-r-[4px]"
												>
													<IoClose className="w-[12px] h-[12px]" />
												</button>
											</div>
										))}
									</div>
								))}
						</div>
					)}
				</>
			) : null}
		</>
	);
};

export default CandidateFilters;
